import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Form, Button, Card, Row, Col, Badge, Alert,
    InputGroup, Spinner, Modal, Table, Tab, Tabs
} from 'react-bootstrap';
import {
    collection, addDoc, getDoc, doc, updateDoc, query, where, getDocs,
    serverTimestamp, getFirestore, orderBy
} from 'firebase/firestore';
import firebaseApp from '../../firebase/credenciales';
import {
    FaBriefcase, FaBuilding, FaCalendarAlt, FaGraduationCap, FaPlus,
    FaLanguage, FaMoneyBillWave, FaMapMarkerAlt, FaSave,
    FaUserTie, FaInfoCircle, FaHistory, FaListUl, FaSearch, FaTimes, FaTrash
} from 'react-icons/fa';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import axios from 'axios';
import { FUNCTION_URLS } from '../../config/functionUrls';

const VacanteFormScreen = ({ usuario }) => {
    const { id } = useParams(); // Para edición de vacante existente
    const navigate = useNavigate();
    const db = getFirestore(firebaseApp);

    // Estados para gestionar el formulario y sus complementos
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [vacante, setVacante] = useState(null);
    const [showAreaModal, setShowAreaModal] = useState(false);
    const [areas, setAreas] = useState([]);
    const [showSkillsModal, setShowSkillsModal] = useState(false);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [historialCambios, setHistorialCambios] = useState([]);
    const [postulaciones, setPostulaciones] = useState([]);

    // NUEVO: Estados para colecciones de Firestore
    const [habilidades, setHabilidades] = useState([]);
    const [profesiones, setProfesiones] = useState([]);
    const [idiomasDisponibles, setIdiomasDisponibles] = useState([]);
    const [loadingHabilidades, setLoadingHabilidades] = useState(false);
    const [loadingProfesiones, setLoadingProfesiones] = useState(false);
    const [loadingIdiomas, setLoadingIdiomas] = useState(false);
    const [perfiles, setPerfiles] = useState([]);
    const [loadingPerfiles, setLoadingPerfiles] = useState(false);

    // Estado adicional para gestionar la visibilidad de secciones opcionales
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Obtener RUC y nombre de la empresa del usuario autenticado
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';
    const userEmail = usuario?.email || '';
    const userRole = usuario?.rol || 'usuario';
    const AXIS_ORQUESTADOR_URL = FUNCTION_URLS.axisOrquestador;
    const HIRO_EVALUAR_URL = FUNCTION_URLS.hiroEvaluarCandidatos;

    // Esquema de validación con Yup
    const validationSchema = Yup.object({
        perfil: Yup.string()
            .required('El perfil es obligatorio'),
        titulo: Yup.string()
            .required('El título de la vacante es obligatorio')
            .min(5, 'El título debe tener al menos 5 caracteres'),
        area: Yup.string()
            .required('El área es obligatoria'),
        descripcion: Yup.string()
            .required('La descripción del puesto es obligatoria')
            .min(30, 'La descripción debe ser más detallada (mínimo 30 caracteres)'),
        ciudad: Yup.string()
            .required('La ciudad es obligatoria'),
        modalidad: Yup.string()
            .required('La modalidad de trabajo es obligatoria'),
        experienciaMinima: Yup.number()
            .required('La experiencia mínima es obligatoria')
            .min(0, 'No puede ser un valor negativo'),
        salarioMinimo: Yup.number()
            .min(0, 'No puede ser un valor negativo'),
        salarioMaximo: Yup.number()
            .min(0, 'No puede ser un valor negativo')
            .test('is-greater', 'Debe ser mayor que el salario mínimo', function (value) {
                const { salarioMinimo } = this.parent;
                return !value || !salarioMinimo || value >= salarioMinimo;
            }),
        fechaInicio: Yup.date()
            .min(new Date(), 'La fecha de inicio no puede ser anterior a hoy'),
        fechaCierre: Yup.date()
            .min(Yup.ref('fechaInicio'), 'La fecha de cierre debe ser posterior a la fecha de inicio'),
    });

    // Configuración inicial de Formik
    const formik = useFormik({
        initialValues: {
            perfil: '',
            titulo: '',
            area: '',
            descripcion: '',
            ciudad: '',
            modalidad: 'presencial', // Valores por defecto
            tipoContrato: 'indefinido',
            experienciaMinima: 0,
            nivelEducativo: 'universitaria',
            profesion: '',
            especialidad: '',
            salarioMinimo: 0,
            salarioMaximo: 0,
            mostrarSalario: false,
            fechaInicio: '',
            fechaCierre: '',
            idiomas: [{ idioma: '', nivel: 'basico', esRequisito: false }],
            habilidades: [],
            responsabilidades: '',
            beneficios: '',
            requisitosAdicionales: '',
            publicaEmpresa: true
        },
        validationSchema,
        onSubmit: handleSubmit
    });

    // Cargar datos iniciales (áreas, vacante existente, etc.)
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                // Cargar áreas de la empresa
                await loadAreas();

                // NUEVO: Cargar datos de colecciones
                await Promise.all([
                    loadHabilidades(),
                    loadProfesiones(),
                    loadIdiomas()
                ]);

                // Si es edición, cargar datos de la vacante
                if (id) {
                    await loadVacante(id);
                    await loadHistorialCambios(id);
                    await loadPostulaciones(id);
                }
            } catch (error) {
                console.error("Error cargando datos iniciales:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudieron cargar los datos iniciales. Por favor, inténtelo nuevamente.'
                });
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [id, ruc]);

    const loadPerfiles = async () => {
        setLoadingPerfiles(true);
        try {
            const perfilesRef = collection(db, 'rs_perfiles');
            const q = query(
                perfilesRef,
                where('ruc', '==', ruc)
            );
            const snapshot = await getDocs(q);

            const perfilesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setPerfiles(perfilesList);
        } catch (error) {
            console.error("Error cargando perfiles:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar los perfiles. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setLoadingPerfiles(false);
        }
    };

    // 3. Modificar el useEffect para cargar también los perfiles
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                // Cargar áreas de la empresa
                await loadAreas();

                // MODIFICADO: Cargar datos de colecciones incluyendo perfiles
                await Promise.all([
                    loadHabilidades(),
                    loadProfesiones(),
                    loadIdiomas(),
                    loadPerfiles() // Añadimos la carga de perfiles
                ]);

                // Si es edición, cargar datos de la vacante
                if (id) {
                    await loadVacante(id);
                    await loadHistorialCambios(id);
                    await loadPostulaciones(id);
                }
            } catch (error) {
                console.error("Error cargando datos iniciales:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudieron cargar los datos iniciales. Por favor, inténtelo nuevamente.'
                });
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [id, ruc]);

    // Función para cargar los datos del perfil seleccionado
    const handlePerfilChange = async (perfilId) => {
        if (!perfilId) return;

        try {
            // Buscar si el perfil ya está en la lista cargada
            let perfilSeleccionado = perfiles.find(p => p.id === perfilId);

            // Si no está en la lista (poco probable), lo cargamos de Firestore
            if (!perfilSeleccionado) {
                const perfilDoc = await getDoc(doc(db, 'rs_perfiles', perfilId));
                if (perfilDoc.exists()) {
                    perfilSeleccionado = { id: perfilDoc.id, ...perfilDoc.data() };
                } else {
                    return; // No se encontró el perfil
                }
            }

            // Rellenar automáticamente los campos con la información del perfil
            formik.setValues({
                ...formik.values,
                perfil: perfilId,
                titulo: perfilSeleccionado.nombre || formik.values.titulo,
                experienciaMinima: perfilSeleccionado.nivelExperiencia || formik.values.experienciaMinima,
                nivelEducativo: perfilSeleccionado.profesiones?.[0]?.nivelEducativo || formik.values.nivelEducativo,
                profesion: perfilSeleccionado.profesionId || formik.values.profesion,
                especialidad: perfilSeleccionado.especialidades?.join(', ') || formik.values.especialidad,
                responsabilidades: perfilSeleccionado.funciones || formik.values.responsabilidades,
                requisitosAdicionales: perfilSeleccionado.requisitosAdicionales || formik.values.requisitosAdicionales,
            });

            // Si el perfil tiene habilidades, las cargamos
            if (perfilSeleccionado.habilidades && perfilSeleccionado.habilidades.length > 0) {
                setSelectedSkills(perfilSeleccionado.habilidades);
                formik.setFieldValue('habilidades', perfilSeleccionado.habilidades);
            }

            // Si hay campos adicionales con información, mostramos la sección avanzada
            if (perfilSeleccionado.funciones || perfilSeleccionado.requisitosAdicionales) {
                setShowAdvanced(true);
            }
        } catch (error) {
            console.error("Error cargando datos del perfil:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar los datos del perfil seleccionado.'
            });
        }
    };

    // NUEVO: Función para cargar habilidades desde Firestore
    const loadHabilidades = async () => {
        setLoadingHabilidades(true);
        try {
            const habilidadesRef = collection(db, 'rs_habilidades');
            const q = query(
                habilidadesRef,
                where('ruc', '==', ruc)
            );
            const snapshot = await getDocs(q);

            const habilidadesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setHabilidades(habilidadesList);
        } catch (error) {
            console.error("Error cargando habilidades:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar las habilidades. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setLoadingHabilidades(false);
        }
    };

    // NUEVO: Función para cargar profesiones desde Firestore
    const loadProfesiones = async () => {
        setLoadingProfesiones(true);
        try {
            const profesionesRef = collection(db, 'rs_profesiones');
            const q = query(
                profesionesRef,
                where('ruc', '==', ruc)
            );
            const snapshot = await getDocs(q);

            const profesionesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setProfesiones(profesionesList);
        } catch (error) {
            console.error("Error cargando profesiones:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar las profesiones. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setLoadingProfesiones(false);
        }
    };

    // NUEVO: Función para cargar idiomas desde Firestore o usar un catálogo predefinido
    const loadIdiomas = async () => {
        setLoadingIdiomas(true);
        try {
            // Intentar cargar desde rs_idiomas (si existe la colección)
            try {
                const idiomasRef = collection(db, 'rs_idiomas');
                const snapshot = await getDocs(idiomasRef);

                if (!snapshot.empty) {
                    const idiomasList = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setIdiomasDisponibles(idiomasList);
                    return;
                }
            } catch (error) {
                console.log("La colección rs_idiomas no existe, usando catálogo predefinido");
            }

            // Si no existe la colección o está vacía, usar un catálogo predefinido
            setIdiomasDisponibles([
                { id: 'es', nombre: 'Español' },
                { id: 'en', nombre: 'Inglés' },
                { id: 'fr', nombre: 'Francés' },
                { id: 'de', nombre: 'Alemán' },
                { id: 'it', nombre: 'Italiano' },
                { id: 'pt', nombre: 'Portugués' },
                { id: 'zh', nombre: 'Chino (Mandarín)' },
                { id: 'ja', nombre: 'Japonés' },
                { id: 'ru', nombre: 'Ruso' },
                { id: 'ar', nombre: 'Árabe' }
            ]);
        } catch (error) {
            console.error("Error cargando idiomas:", error);
        } finally {
            setLoadingIdiomas(false);
        }
    };

    // Función para cargar áreas de la empresa
    const loadAreas = async () => {
        try {
            const areasRef = collection(db, 'areas'); // Mantén el nombre consistente
            const q = query(areasRef, where('ruc', '==', ruc), orderBy('nombre'));
            const snapshot = await getDocs(q);

            const areasList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setAreas(areasList);
        } catch (error) {
            console.error("Error cargando áreas:", error);
        }
    };

    // Función para cargar una vacante existente (modo edición)
    const loadVacante = async (vacanteId) => {
        try {
            const vacanteRef = doc(db, 'rs_vacantes', vacanteId);
            const vacanteDoc = await getDoc(vacanteRef);

            if (vacanteDoc.exists()) {
                const vacanteData = vacanteDoc.data();
                setVacante({ id: vacanteId, ...vacanteData });

                console.log("Vacante cargada:", vacanteData); // Añadir log para debug

                // Convertir fechas de Firestore a formato de formulario
                const fechaInicio = vacanteData.fechaInicio ? new Date(vacanteData.fechaInicio.toDate()).toISOString().split('T')[0] : '';
                const fechaCierre = vacanteData.fechaCierre ? new Date(vacanteData.fechaCierre.toDate()).toISOString().split('T')[0] : '';

                // MODIFICADO: Manejar habilidades como objetos completos
                const habilidadesIds = vacanteData.habilidades || [];
                const habilidadesSeleccionadas = Array.isArray(habilidadesIds) && typeof habilidadesIds[0] === 'object'
                    ? habilidadesIds
                    : []; // Si ya son objetos, los mantenemos

                // Establecer habilidades seleccionadas
                setSelectedSkills(habilidadesSeleccionadas);

                // Actualizar valores de formik
                formik.setValues({
                    perfil: vacanteData.perfil || '',
                    titulo: vacanteData.titulo || '',
                    area: vacanteData.area || '',
                    descripcion: vacanteData.descripcion || '',
                    ciudad: vacanteData.ciudad || '',
                    modalidad: vacanteData.modalidad || 'presencial',
                    tipoContrato: vacanteData.tipoContrato || 'indefinido',
                    experienciaMinima: vacanteData.experienciaMinima || 0,
                    nivelEducativo: vacanteData.nivelEducativo || 'universitaria',
                    profesion: vacanteData.profesion || '',
                    especialidad: vacanteData.especialidad || '',
                    salarioMinimo: vacanteData.salarioMinimo || 0,
                    salarioMaximo: vacanteData.salarioMaximo || 0,
                    mostrarSalario: vacanteData.mostrarSalario || false,
                    fechaInicio: fechaInicio,
                    fechaCierre: fechaCierre,
                    idiomas: vacanteData.idiomas || [{ idioma: '', nivel: 'basico', esRequisito: false }],
                    habilidades: habilidadesSeleccionadas, // MODIFICADO: Ahora son objetos completos
                    responsabilidades: vacanteData.responsabilidades || '',
                    beneficios: vacanteData.beneficios || '',
                    requisitosAdicionales: vacanteData.requisitosAdicionales || '',
                    publicaEmpresa: vacanteData.publicaEmpresa !== undefined ? vacanteData.publicaEmpresa : true
                });

                // Mostrar sección avanzada si la vacante tiene datos en esos campos
                if (
                    vacanteData.responsabilidades ||
                    vacanteData.beneficios ||
                    vacanteData.requisitosAdicionales ||
                    (vacanteData.idiomas && vacanteData.idiomas.length > 0 && vacanteData.idiomas[0].idioma)
                ) {
                    setShowAdvanced(true);
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Vacante no encontrada',
                    text: 'La vacante que intenta editar no existe.'
                });
                navigate('/vacantes');
            }
        } catch (error) {
            console.error("Error cargando vacante:", error);
        }
    };

    // Cargar historial de cambios de la vacante
    const loadHistorialCambios = async (vacanteId) => {
        try {
            const historialRef = collection(db, 'rs_vacantes_historial');
            let snapshot;
            try {
                const q = query(
                    historialRef,
                    where('vacanteId', '==', vacanteId),
                    orderBy('fecha', 'desc')
                );
                snapshot = await getDocs(q);
            } catch (indexError) {
                // Fallback sin orderBy para evitar bloqueo mientras se crea el índice compuesto.
                const qNoOrder = query(
                    historialRef,
                    where('vacanteId', '==', vacanteId)
                );
                snapshot = await getDocs(qNoOrder);
            }

            const historial = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).sort((a, b) => {
                const getMs = (v) => {
                    if (!v) return 0;
                    if (typeof v?.toDate === "function") return v.toDate().getTime();
                    const d = new Date(v);
                    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
                };
                return getMs(b.fecha) - getMs(a.fecha);
            });

            setHistorialCambios(historial);
        } catch (error) {
            console.error("Error cargando historial:", error);
        }
    };

    // Cargar postulaciones a esta vacante
    const loadPostulaciones = async (vacanteId) => {
        try {
            const postulacionesRef = collection(db, 'rs_postulaciones');
            let snapshot;
            try {
                const q = query(
                    postulacionesRef,
                    where('vacanteId', '==', vacanteId),
                    orderBy('fechaPostulacion', 'desc')
                );
                snapshot = await getDocs(q);
            } catch (indexError) {
                // Fallback sin orderBy para evitar bloqueo mientras se crea el índice compuesto.
                const qNoOrder = query(
                    postulacionesRef,
                    where('vacanteId', '==', vacanteId)
                );
                snapshot = await getDocs(qNoOrder);
            }

            const postulacionesData = [];

            // Para cada postulación, obtener datos del candidato
            for (const postulacionDoc of snapshot.docs) {
                const postulacion = postulacionDoc.data();

                let candidato = null;
                if (postulacion.candidatoId) {
                    const candidatoDoc = await getDoc(doc(db, 'candidatos', postulacion.candidatoId));
                    if (candidatoDoc.exists()) {
                        candidato = candidatoDoc.data();
                    }
                }

                postulacionesData.push({
                    id: postulacionDoc.id,
                    ...postulacion,
                    candidato
                });
            }

            postulacionesData.sort((a, b) => {
                const getMs = (v) => {
                    if (!v) return 0;
                    if (typeof v?.toDate === "function") return v.toDate().getTime();
                    const d = new Date(v);
                    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
                };
                return getMs(b.fechaPostulacion) - getMs(a.fechaPostulacion);
            });

            setPostulaciones(postulacionesData);
        } catch (error) {
            console.error("Error cargando postulaciones:", error);
        }
    };

        // Función para guardar la vacante (nueva o edición)
    async function handleSubmit(values, { setSubmitting }) {
        console.log("Form submitted, values:", values); // Log inicial con todos los valores
        console.log("Validation errors:", formik.errors); // Ver si hay errores de validación
        
        try {
            // Validaciones manuales adicionales
            if (!values.perfil) {
                console.error("Perfil no seleccionado");
                Swal.fire({
                    icon: 'error',
                    title: 'Error de validación',
                    text: 'Debe seleccionar un perfil para la vacante'
                });
                setSubmitting(false);
                return;
            }
            
            // Validación adicional para área
            if (!values.area) {
                console.error("Área no seleccionada");
                Swal.fire({
                    icon: 'error',
                    title: 'Error de validación',
                    text: 'Debe seleccionar un área para la vacante'
                });
                setSubmitting(false);
                return;
            }
    
            // Validación adicional para título
            if (!values.titulo || values.titulo.length < 5) {
                console.error("Título inválido");
                Swal.fire({
                    icon: 'error',
                    title: 'Error de validación',
                    text: 'El título de la vacante debe tener al menos 5 caracteres'
                });
                setSubmitting(false);
                return;
            }
            
            // Validación para descripción
            if (!values.descripcion || values.descripcion.length < 30) {
                console.error("Descripción inválida");
                Swal.fire({
                    icon: 'error',
                    title: 'Error de validación',
                    text: 'La descripción debe tener al menos 30 caracteres'
                });
                setSubmitting(false);
                return;
            }
            
            // Validación para fecha de inicio
            if (!values.fechaInicio) {
                console.error("Fecha de inicio no especificada");
                Swal.fire({
                    icon: 'error',
                    title: 'Error de validación',
                    text: 'Debe especificar una fecha de inicio para la publicación'
                });
                setSubmitting(false);
                return;
            }
    
            console.log("Pasó todas las validaciones manuales, procediendo a guardar...");
            setSaving(true);
    
            // Preparar objeto de vacante para Firestore
            const vacanteData = {
                ...values,
                fechaInicio: values.fechaInicio ? new Date(values.fechaInicio) : null,
                fechaCierre: values.fechaCierre ? new Date(values.fechaCierre) : null,
                ruc,
                empresa: nombreEmpresa,
                // Asegurar que valores numéricos sean números
                experienciaMinima: Number(values.experienciaMinima),
                salarioMinimo: Number(values.salarioMinimo),
                salarioMaximo: Number(values.salarioMaximo),
                // Asegurar que el área se guarde correctamente
                area: values.area || '',
                // Buscar y añadir el CECO asociado al área seleccionada
                areaCeco: areas.find(area => area.nombre === values.area)?.ceco || ''
            };
            
            console.log("Objeto preparado para Firestore:", vacanteData);
    
            // Si es una nueva vacante
            if (!id) {
                console.log("Creando nueva vacante...");
                // Añadir campos adicionales para nuevas vacantes
                vacanteData.fechaCreacion = serverTimestamp();
                vacanteData.estado = 'activa';
                vacanteData.creadoPor = {
                    email: userEmail,
                    rol: userRole
                };
                vacanteData.cantidadPostulaciones = 0;
    
                try {
                    // Guardar en Firestore - Asegúrate de usar siempre la misma colección
                    console.log("Guardando en Firestore...");
                    const docRef = await addDoc(collection(db, 'rs_vacantes'), vacanteData);
                    console.log("Vacante creada con ID:", docRef.id);
    
                    // Registrar en historial
                    await addDoc(collection(db, 'rs_vacantes_historial'), {
                        vacanteId: docRef.id,
                        accion: 'creacion',
                        fecha: serverTimestamp(),
                        usuario: userEmail,
                        detalles: 'Creación inicial de la vacante'
                    });
                    console.log("Historial registrado");

                    // Activación de Axis/Hiro temporalmente deshabilitada en creación por contingencia.
    
                    Swal.fire({
                        icon: 'success',
                        title: '¡Vacante creada!',
                        text: 'La vacante ha sido creada exitosamente.',
                        confirmButtonText: 'Ver vacantes'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            navigate('/vacantes');
                        }
                    });
                } catch (innerError) {
                    console.error("Error específico al guardar en Firestore:", innerError);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error al guardar',
                        text: `Ocurrió un error al guardar en la base de datos: ${innerError.message}`
                    });
                }
            }
            // Si es edición de vacante existente
            else {
                console.log("Actualizando vacante existente...");
                // Añadir campos de actualización
                vacanteData.fechaActualizacion = serverTimestamp();
                vacanteData.actualizadoPor = {
                    email: userEmail,
                    rol: userRole
                };
    
                try {
                    // Actualizar en Firestore
                    await updateDoc(doc(db, 'rs_vacantes', id), vacanteData);
                    console.log("Vacante actualizada con éxito");
    
                    // Registrar en historial
                    await addDoc(collection(db, 'rs_vacantes_historial'), {
                        vacanteId: id,
                        accion: 'actualizacion',
                        fecha: serverTimestamp(),
                        usuario: userEmail,
                        detalles: 'Actualización de la información de la vacante'
                    });
                    console.log("Historial de actualización registrado");
    
                    // Recargar historial después de actualizar
                    await loadHistorialCambios(id);

                    // Activación temporal Axis/Hiro al editar (contingencia)
                    (async () => {
                        try {
                            const axisResponse = await axios.post(
                                AXIS_ORQUESTADOR_URL,
                                {
                                    systemAction: "iniciar_reclutamiento_hiro",
                                    vacanteId: id,
                                    ruc,
                                    empresa: nombreEmpresa,
                                    maxResults: 10
                                },
                                { headers: { "Content-Type": "application/json" } }
                            );

                            const axisData = axisResponse?.data || {};
                            const top5 = Array.isArray(axisData.candidatos) ? axisData.candidatos.slice(0, 5) : [];

                            await updateDoc(doc(db, 'rs_vacantes', id), {
                                hiroProcesoId: axisData.procesoId || null,
                                hiroRankingTop5: top5,
                                hiroLastEvaluationAt: serverTimestamp(),
                                hiroLastEvaluationStatus: axisData.success ? "ok" : "error"
                            });

                            await addDoc(collection(db, 'rs_vacantes_historial'), {
                                vacanteId: id,
                                accion: 'hiro_ranking_inicial',
                                fecha: serverTimestamp(),
                                usuario: userEmail,
                                detalles: `Axis/Hiro inició proceso ${axisData.procesoId || "N/A"} y generó ranking inicial de ${top5.length} candidatos`
                            });
                        } catch (axisError) {
                            console.error("Error iniciando proceso Axis/Hiro:", axisError);
                            try {
                                const hiroResponse = await axios.post(
                                    HIRO_EVALUAR_URL,
                                    {
                                        vacanteId: id,
                                        ruc,
                                        empresa: nombreEmpresa,
                                        maxResults: 10
                                    },
                                    { headers: { "Content-Type": "application/json" } }
                                );
                                const hiroData = hiroResponse?.data || {};
                                const top5Hiro = Array.isArray(hiroData.candidatos) ? hiroData.candidatos.slice(0, 5) : [];

                                await updateDoc(doc(db, 'rs_vacantes', id), {
                                    hiroProcesoId: hiroData.procesoId || null,
                                    hiroRankingTop5: top5Hiro,
                                    hiroLastEvaluationAt: serverTimestamp(),
                                    hiroLastEvaluationStatus: "ok_fallback_hiro"
                                });

                                await addDoc(collection(db, 'rs_vacantes_historial'), {
                                    vacanteId: id,
                                    accion: 'hiro_ranking_fallback',
                                    fecha: serverTimestamp(),
                                    usuario: userEmail,
                                    detalles: `Axis falló (${axisError.message || "error"}). Hiro directo generó ranking inicial de ${top5Hiro.length} candidatos`
                                });
                            } catch (hiroError) {
                                console.error("Error en fallback directo a Hiro:", hiroError);
                                await addDoc(collection(db, 'rs_vacantes_historial'), {
                                    vacanteId: id,
                                    accion: 'hiro_ranking_error',
                                    fecha: serverTimestamp(),
                                    usuario: userEmail,
                                    detalles: `Falló Axis y también Hiro directo: ${hiroError.message || "Error desconocido"}`
                                });
                            }
                        }
                    })();

                    Swal.fire({
                        icon: 'success',
                        title: '¡Vacante actualizada!',
                        text: 'Los cambios han sido guardados exitosamente.'
                    });
                } catch (innerError) {
                    console.error("Error específico al actualizar en Firestore:", innerError);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error al actualizar',
                        text: `Ocurrió un error al actualizar en la base de datos: ${innerError.message}`
                    });
                }
            }
        } catch (error) {
            console.error("Error guardando vacante:", error);
            console.error("Stack trace:", error.stack);
    
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al guardar la vacante. Por favor, inténtelo nuevamente.'
            });
        } finally {
            console.log("Finalizando proceso de guardado");
            setSaving(false);
            setSubmitting(false);
        }
    }

    // Mostrar spinner de carga mientras se cargan los datos iniciales
    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Cargando información...</p>
                </div>
            </Container>
        );
    }

    // Función para agregar un nuevo idioma al formulario
    const handleAddIdioma = () => {
        const idiomas = [...formik.values.idiomas];
        idiomas.push({ idioma: '', nivel: 'basico', esRequisito: false });
        formik.setFieldValue('idiomas', idiomas);
    };

    // Función para eliminar un idioma del formulario
    const handleRemoveIdioma = (index) => {
        const idiomas = [...formik.values.idiomas];
        idiomas.splice(index, 1);
        formik.setFieldValue('idiomas', idiomas);
    };

    // Función para manejar cambios en los idiomas
    const handleIdiomaChange = (index, field, value) => {
        const idiomas = [...formik.values.idiomas];
        idiomas[index][field] = value;
        formik.setFieldValue('idiomas', idiomas);
    };

    // MODIFICADO: Función para agregar una habilidad
    const handleAddSkill = (habilidadId) => {
        if (!habilidadId) return;

        // Buscar la habilidad en la lista de habilidades
        const habilidad = habilidades.find(h => h.id === habilidadId);

        if (habilidad && !selectedSkills.some(s => s.id === habilidad.id)) {
            const newSelectedSkills = [...selectedSkills, {
                id: habilidad.id,
                nombre: habilidad.nombre,
                categoria: habilidad.categoria || ''
            }];

            setSelectedSkills(newSelectedSkills);
            formik.setFieldValue('habilidades', newSelectedSkills);
        }
    };

    // MODIFICADO: Función para eliminar una habilidad
    const handleRemoveSkill = (habilidadId) => {
        const newSelectedSkills = selectedSkills.filter(s => s.id !== habilidadId);
        setSelectedSkills(newSelectedSkills);
        formik.setFieldValue('habilidades', newSelectedSkills);
    };

    // Función para agregar una nueva área
    const handleAddArea = async (nombreArea, cecoArea = '') => {
        if (!nombreArea.trim()) return;

        try {
            // Verificar si ya existe esta área
            const areasRef = collection(db, 'areas');
            const q = query(
                areasRef,
                where('ruc', '==', ruc),
                where('nombre', '==', nombreArea.trim())
            );

            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Área existente',
                    text: 'Esta área ya existe en su empresa.'
                });
                return;
            }

            // Crear nueva área con CECO
            const nuevaAreaRef = await addDoc(collection(db, 'areas'), {
                nombre: nombreArea.trim(),
                ceco: cecoArea.trim(),  // Añadimos el campo CECO
                ruc,
                empresa: nombreEmpresa,
                fechaCreacion: serverTimestamp()
            });

            // Añadir el área a la lista local de áreas
            const nuevaArea = {
                id: nuevaAreaRef.id,
                nombre: nombreArea.trim(),
                ceco: cecoArea.trim(),  // También lo incluimos en el estado local
                ruc,
                empresa: nombreEmpresa
            };

            setAreas(prevAreas => [...prevAreas, nuevaArea]);

            // Seleccionar automáticamente el área recién creada
            formik.setFieldValue('area', nombreArea.trim());

            // Cerrar modal
            setShowAreaModal(false);

            Swal.fire({
                icon: 'success',
                title: 'Área creada',
                text: 'Se ha creado una nueva área correctamente.'
            });
        } catch (error) {
            console.error("Error creando área:", error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al crear el área. Por favor, inténtelo nuevamente.'
            });
        }
    };

    // Función auxiliar para formatear fechas
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return "Fecha inválida";
        }
    };

    return (
        <Container fluid className="py-4">
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">
                        {id ? 'Editar Vacante' : 'Crear Nueva Vacante'}
                    </h4>
                    {id && (
                        <Badge
                            bg={vacante?.estado === 'activa' ? 'success' : 'danger'}
                            className="fs-6"
                        >
                            {vacante?.estado === 'activa' ? 'Activa' : 'Inactiva'}
                        </Badge>
                    )}
                </Card.Header>

                <Card.Body>
                    {id ? (
                        <Tabs defaultActiveKey="info" className="mb-4">
                            <Tab eventKey="info" title={<span><FaInfoCircle className="me-2" />Información</span>}>
                                <div className="py-3">
                                    {renderForm()}
                                </div>
                            </Tab>
                            <Tab eventKey="historial" title={<span><FaHistory className="me-2" />Historial de Cambios</span>}>
                                <div className="py-3">
                                    {renderHistorial()}
                                </div>
                            </Tab>
                            <Tab eventKey="postulaciones" title={<span><FaUserTie className="me-2" />Postulaciones</span>}>
                                <div className="py-3">
                                    {renderPostulaciones()}
                                </div>
                            </Tab>
                        </Tabs>
                    ) : (
                        renderForm()
                    )}
                </Card.Body>
            </Card>

            {/* Modal para añadir área */}
            <Modal show={showAreaModal} onHide={() => setShowAreaModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Agregar Nueva Área</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        const nombreArea = e.target.elements.nombreArea.value;
                        const cecoArea = e.target.elements.cecoArea.value;
                        handleAddArea(nombreArea, cecoArea);
                    }}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre del Área</Form.Label>
                            <Form.Control
                                type="text"
                                name="nombreArea"
                                placeholder="Ej: Recursos Humanos"
                                required
                            />
                            <Form.Text className="text-muted">
                                Ingrese el nombre del área o departamento de su empresa.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Centro de Costo (CECO)</Form.Label>
                            <Form.Control
                                type="text"
                                name="cecoArea"
                                placeholder="Ej: RH001"
                                pattern="[A-Za-z0-9]+"
                                title="Solo se permiten letras y números"
                                required
                            />
                            <Form.Text className="text-muted">
                                Ingrese el código de centro de costo asociado a esta área.
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowAreaModal(false)}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit">
                                Guardar Área
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* MODIFICADO: Modal para gestionar habilidades desde rs_habilidades */}
            <Modal show={showSkillsModal} onHide={() => setShowSkillsModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Gestionar Habilidades y Competencias</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingHabilidades ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">Cargando habilidades...</span>
                        </div>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Seleccionar Habilidad o Competencia</Form.Label>
                                <Form.Select
                                    onChange={(e) => handleAddSkill(e.target.value)}
                                    value=""
                                >
                                    <option value="">Seleccionar habilidad...</option>
                                    {habilidades
                                        .filter(h => !selectedSkills.some(s => s.id === h.id))
                                        .map(habilidad => (
                                            <option key={habilidad.id} value={habilidad.id}>
                                                {habilidad.nombre} {habilidad.categoria ? `(${habilidad.categoria})` : ''}
                                            </option>
                                        ))
                                    }
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Seleccione las habilidades y competencias requeridas para este puesto.
                                </Form.Text>
                            </Form.Group>

                            <div className="mt-4">
                                <h6>Habilidades Seleccionadas</h6>
                                {selectedSkills.length === 0 ? (
                                    <p className="text-muted small">No hay habilidades seleccionadas</p>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {selectedSkills.map((skill) => (
                                            <Badge
                                                key={skill.id}
                                                bg={skill.categoria ? "info" : "secondary"}
                                                className="d-flex align-items-center p-2"
                                            >
                                                {skill.nombre}
                                                <Button
                                                    variant="link"
                                                    className="p-0 ms-2 text-white"
                                                    onClick={() => handleRemoveSkill(skill.id)}
                                                >
                                                    <FaTimes size={12} />
                                                </Button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="primary" onClick={() => setShowSkillsModal(false)}>
                            Aceptar
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    );

    // Función para renderizar el formulario principal
    function renderForm() {
        return (
            <Form onSubmit={formik.handleSubmit}>
                <Row className="mb-4">
                    <Col>
                        <Alert variant="info">
                            <div className="d-flex align-items-center">
                                <FaInfoCircle className="me-2" size={18} />
                                <div>
                                    <strong>Información importante: </strong>
                                    Complete todos los campos obligatorios (*) para crear una vacante efectiva.
                                </div>
                            </div>
                        </Alert>
                    </Col>
                </Row>

                <Card className="mb-4">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0 d-flex align-items-center">
                            <FaBriefcase className="me-2" />
                            Información Básica del Puesto
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="perfil">
                                    <Form.Label>Perfil de la Vacante *</Form.Label>
                                    <InputGroup>
                                        <Form.Select
                                            value={formik.values.perfil}
                                            onChange={(e) => {
                                                const perfilId = e.target.value;
                                                formik.setFieldValue('perfil', perfilId);
                                                handlePerfilChange(perfilId);
                                            }}
                                            isInvalid={formik.touched.perfil && !!formik.errors.perfil}
                                            disabled={loadingPerfiles}
                                        >
                                            <option value="">Seleccione un perfil</option>
                                            {loadingPerfiles ? (
                                                <option value="" disabled>Cargando perfiles...</option>
                                            ) : (
                                                perfiles.map(perfil => (
                                                    <option key={perfil.id} value={perfil.id}>
                                                        {perfil.nombre} {perfil.codigo ? `(${perfil.codigo})` : ''}
                                                    </option>
                                                ))
                                            )}
                                        </Form.Select>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => navigate('/catalogos/perfiles')}
                                            title="Ir a gestión de perfiles"
                                        >
                                            <FaPlus />
                                        </Button>
                                    </InputGroup>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.perfil}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-muted">
                                        Al seleccionar un perfil, se completarán automáticamente varios campos del formulario.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
        <Form.Group controlId="area">
            <Form.Label>Área de la Vacante *</Form.Label>
            <InputGroup>
                <Form.Select
                    {...formik.getFieldProps('area')}
                    isInvalid={formik.touched.area && !!formik.errors.area}
                >
                    <option value="">Seleccione un área</option>
                    {areas.map(area => (
                        <option key={area.id} value={area.nombre}>
                            {area.nombre} {area.ceco ? `(${area.ceco})` : ''}
                        </option>
                    ))}
                </Form.Select>
                <Button 
                    variant="outline-secondary" 
                    onClick={() => setShowAreaModal(true)}
                >
                    <FaPlus />
                </Button>
            </InputGroup>
            <Form.Control.Feedback type="invalid">
                {formik.errors.area}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
                Seleccione el área o departamento donde se ubicará esta vacante
            </Form.Text>
        </Form.Group>
    </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="modalidad">
                                    <Form.Label>Modalidad de Trabajo *</Form.Label>
                                    <Form.Select
                                        {...formik.getFieldProps('modalidad')}
                                        isInvalid={formik.touched.modalidad && !!formik.errors.modalidad}
                                    >
                                        <option value="presencial">Presencial</option>
                                        <option value="remoto">Remoto</option>
                                        <option value="hibrido">Híbrido</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.modalidad}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="ciudad">
                                    <Form.Label>Ciudad/Ubicación *</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <FaMapMarkerAlt />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Ej: Lima"
                                            {...formik.getFieldProps('ciudad')}
                                            isInvalid={formik.touched.ciudad && !!formik.errors.ciudad}
                                        />
                                    </InputGroup>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.ciudad}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3" controlId="descripcion">
                            <Form.Label>Descripción del Puesto *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Describa las responsabilidades principales y el propósito del puesto"
                                {...formik.getFieldProps('descripcion')}
                                isInvalid={formik.touched.descripcion && !!formik.errors.descripcion}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.descripcion}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                Mínimo 30 caracteres. Una buena descripción atrae a mejores candidatos.
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="mb-4">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0 d-flex align-items-center">
                            <FaUserTie className="me-2" />
                            Requisitos del Candidato
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        {formik.values.perfil && (
                            <Alert variant="info" className="mb-3">
                                <small>
                                    <FaInfoCircle className="me-1" />
                                    Los siguientes campos se han completado automáticamente basados en el perfil seleccionado.
                                    Puede modificarlos si necesita personalizar la vacante.
                                </small>
                            </Alert>
                        )}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="experienciaMinima">
                                    <Form.Label>Experiencia Mínima (años) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="1"
                                        {...formik.getFieldProps('experienciaMinima')}
                                        isInvalid={formik.touched.experienciaMinima && !!formik.errors.experienciaMinima}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.experienciaMinima}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="nivelEducativo">
                                    <Form.Label>Nivel Educativo</Form.Label>
                                    <Form.Select
                                        {...formik.getFieldProps('nivelEducativo')}
                                    >
                                        <option value="secundaria">Secundaria</option>
                                        <option value="tecnico">Técnico</option>
                                        <option value="universitaria">Universitaria</option>
                                        <option value="postgrado">Postgrado</option>
                                        <option value="maestria">Maestría</option>
                                        <option value="doctorado">Doctorado</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="profesion">
                                    <Form.Label>Profesión Requerida</Form.Label>
                                    <Form.Select
                                        {...formik.getFieldProps('profesion')}
                                        disabled={loadingProfesiones}
                                    >
                                        <option value="">Seleccione una profesión</option>
                                        {loadingProfesiones ? (
                                            <option value="" disabled>Cargando profesiones...</option>
                                        ) : (
                                            profesiones.map(profesion => (
                                                <option key={profesion.id} value={profesion.id}>
                                                    {profesion.nombre} {profesion.nivelEducativo ? `(${profesion.nivelEducativo})` : ''}
                                                </option>
                                            ))
                                        )}
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        Seleccione la profesión principal requerida para este puesto
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="especialidad">
                                    <Form.Label>Especialidad</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: Desarrollo Web Frontend"
                                        {...formik.getFieldProps('especialidad')}
                                    />
                                    <Form.Text className="text-muted">
                                        Especialización específica requerida (opcional)
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label>Habilidades y Competencias</Form.Label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => setShowSkillsModal(true)}
                                            className="w-100"
                                        >
                                            <FaListUl className="me-2" />
                                            {selectedSkills.length > 0
                                                ? `${selectedSkills.length} habilidades seleccionadas`
                                                : "Seleccionar habilidades"}
                                        </Button>
                                        {loadingHabilidades && (
                                            <Spinner animation="border" size="sm" />
                                        )}
                                    </div>
                                    {selectedSkills.length > 0 && (
                                        <div className="mt-2 d-flex flex-wrap gap-1">
                                            {selectedSkills.slice(0, 3).map(skill => (
                                                <Badge key={skill.id} bg="secondary">
                                                    {skill.nombre}
                                                </Badge>
                                            ))}
                                            {selectedSkills.length > 3 && (
                                                <Badge bg="info">+{selectedSkills.length - 3} más</Badge>
                                            )}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <Form.Label>Idiomas Requeridos</Form.Label>
                                {formik.values.idiomas.map((idioma, index) => (
                                    <div key={index} className="mb-2 border-bottom pb-2">
                                        <Row className="align-items-center mb-2">
                                            <Col md={5}>
                                                <Form.Select
                                                    value={idioma.idioma}
                                                    onChange={(e) => handleIdiomaChange(index, 'idioma', e.target.value)}
                                                    disabled={loadingIdiomas}
                                                >
                                                    <option value="">Seleccione un idioma</option>
                                                    {idiomasDisponibles.map(idiomaOption => (
                                                        <option key={idiomaOption.id} value={idiomaOption.id}>
                                                            {idiomaOption.nombre}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Select
                                                    value={idioma.nivel}
                                                    onChange={(e) => handleIdiomaChange(index, 'nivel', e.target.value)}
                                                >
                                                    <option value="basico">Básico</option>
                                                    <option value="intermedio">Intermedio</option>
                                                    <option value="avanzado">Avanzado</option>
                                                    <option value="nativo">Nativo</option>
                                                </Form.Select>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Check
                                                    type="switch"
                                                    id={`idioma-requisito-${index}`}
                                                    label="Es requisito"
                                                    checked={idioma.esRequisito}
                                                    onChange={(e) => handleIdiomaChange(index, 'esRequisito', e.target.checked)}
                                                />
                                            </Col>
                                            <Col md={1}>
                                                {index > 0 && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveIdioma(index)}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                ))}

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleAddIdioma}
                                    className="mt-2"
                                >
                                    <FaPlus className="me-1" /> Agregar otro idioma
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="mb-4">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0 d-flex align-items-center">
                            <FaMoneyBillWave className="me-2" />
                            Condiciones Laborales
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="tipoContrato">
                                    <Form.Label>Tipo de Contrato</Form.Label>
                                    <Form.Select
                                        {...formik.getFieldProps('tipoContrato')}
                                    >
                                        <option value="indefinido">Indefinido</option>
                                        <option value="determinado">Plazo Determinado</option>
                                        <option value="temporal">Temporal</option>
                                        <option value="parcial">Tiempo Parcial</option>
                                        <option value="practicas">Prácticas</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="salario">
                                    <Form.Label>Rango Salarial</Form.Label>
                                    <Row className="g-2 align-items-center">
                                        <Col>
                                            <InputGroup>
                                                <InputGroup.Text>S/</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Mínimo"
                                                    min="0"
                                                    {...formik.getFieldProps('salarioMinimo')}
                                                    isInvalid={formik.touched.salarioMinimo && !!formik.errors.salarioMinimo}
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col xs="auto">—</Col>
                                        <Col>
                                            <InputGroup>
                                                <InputGroup.Text>S/</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Máximo"
                                                    min="0"
                                                    {...formik.getFieldProps('salarioMaximo')}
                                                    isInvalid={formik.touched.salarioMaximo && !!formik.errors.salarioMaximo}
                                                />
                                            </InputGroup>
                                        </Col>
                                    </Row>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.salarioMinimo || formik.errors.salarioMaximo}
                                    </Form.Control.Feedback>
                                    <Form.Check
                                        type="switch"
                                        id="mostrarSalarioSwitch"
                                        label="Mostrar salario en el anuncio"
                                        className="mt-2"
                                        {...formik.getFieldProps('mostrarSalario')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="fechaInicio">
                                    <Form.Label>Fecha de Inicio de Publicación *</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <FaCalendarAlt />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="date"
                                            {...formik.getFieldProps('fechaInicio')}
                                            isInvalid={formik.touched.fechaInicio && !!formik.errors.fechaInicio}
                                        />
                                    </InputGroup>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.fechaInicio}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="fechaCierre">
                                    <Form.Label>Fecha de Cierre de Vacante</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <FaCalendarAlt />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="date"
                                            {...formik.getFieldProps('fechaCierre')}
                                            isInvalid={formik.touched.fechaCierre && !!formik.errors.fechaCierre}
                                        />
                                    </InputGroup>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.fechaCierre}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Button
                    variant="outline-secondary"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="mb-3 w-100"
                >
                    {showAdvanced ? "Ocultar campos opcionales" : "Mostrar campos opcionales"}
                </Button>

                {showAdvanced && (
                    <Card className="mb-4">
                        <Card.Header className="bg-light">
                            <h5 className="mb-0">Información Adicional (Opcional)</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3" controlId="responsabilidades">
                                <Form.Label>Responsabilidades Detalladas</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Detalle las principales responsabilidades y tareas del puesto"
                                    {...formik.getFieldProps('responsabilidades')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="beneficios">
                                <Form.Label>Beneficios</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Describa los beneficios que ofrece su empresa (Ej: seguro médico, bonos, etc.)"
                                    {...formik.getFieldProps('beneficios')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="requisitosAdicionales">
                                <Form.Label>Requisitos Adicionales</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Otros requisitos específicos no mencionados anteriormente"
                                    {...formik.getFieldProps('requisitosAdicionales')}
                                />
                            </Form.Group>

                            <Form.Group>
                                <Form.Check
                                    type="switch"
                                    id="publicaEmpresaSwitch"
                                    label="Publicar nombre de la empresa"
                                    className="mb-2"
                                    {...formik.getFieldProps('publicaEmpresa')}
                                />
                                <Form.Text className="text-muted">
                                    Si desactiva esta opción, el nombre de su empresa no será visible en el anuncio público.
                                </Form.Text>
                            </Form.Group>
                        </Card.Body>
                    </Card>
                )}

                <div className="d-flex justify-content-between">
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/vacantes')}
                        disabled={saving}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={saving || formik.isSubmitting}
                        onClick={() => {
                            // Añadir verificación de errores silenciosos
                            if (Object.keys(formik.errors).length > 0) {
                                console.error("Errores en el formulario:", formik.errors);
                                alert("Hay errores en el formulario: " + JSON.stringify(formik.errors));
                            }
                        }}
                        className="d-flex align-items-center"
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <FaSave className="me-2" />
                                {id ? 'Actualizar Vacante' : 'Crear Vacante'}
                            </>
                        )}
                    </Button>
                </div>
            </Form>
        );
    }

    // Función para renderizar el historial de cambios
    function renderHistorial() {
        return (
            <>
                {historialCambios.length === 0 ? (
                    <Alert variant="info">
                        No hay registros de cambios para esta vacante.
                    </Alert>
                ) : (
                    <Table responsive hover>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Usuario</th>
                                <th>Acción</th>
                                <th>Detalles</th>
                            </tr>
                        </thead>
                        <tbody>
                            {historialCambios.map(cambio => (
                                <tr key={cambio.id}>
                                    <td>{formatDate(cambio.fecha)}</td>
                                    <td>{cambio.usuario || 'Sistema'}</td>
                                    <td>
                                        <Badge bg={
                                            cambio.accion === 'creacion' ? 'success' :
                                                cambio.accion === 'actualizacion' ? 'primary' :
                                                    cambio.accion === 'estado' ? 'warning' : 'secondary'
                                        }>
                                            {cambio.accion === 'creacion' ? 'Creación' :
                                                cambio.accion === 'actualizacion' ? 'Actualización' :
                                                    cambio.accion === 'estado' ? 'Cambio de estado' : cambio.accion}
                                        </Badge>
                                    </td>
                                    <td>{cambio.detalles || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </>
        );
    }

    // Función para renderizar las postulaciones
    function renderPostulaciones() {
        return (
            <>
                {postulaciones.length === 0 ? (
                    <Alert variant="info">
                        No hay postulaciones para esta vacante.
                    </Alert>
                ) : (
                    <>
                        <div className="mb-3">
                            <Badge bg="info">{postulaciones.length} postulaciones encontradas</Badge>
                        </div>
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Candidato</th>
                                    <th>Fecha</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {postulaciones.map(postulacion => (
                                    <tr key={postulacion.id}>
                                        <td>
                                            {postulacion.candidato ? (
                                                <>
                                                    {postulacion.candidato.nombre} {postulacion.candidato.apellido}
                                                    {postulacion.candidato.profesion && (
                                                        <div className="small text-muted">
                                                            {postulacion.candidato.profesion}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-muted">Candidato no disponible</span>
                                            )}
                                        </td>
                                        <td>{formatDate(postulacion.fechaPostulacion)}</td>
                                        <td>
                                            <Badge bg={
                                                postulacion.estado === 'pendiente' ? 'warning' :
                                                    postulacion.estado === 'revisado' ? 'info' :
                                                        postulacion.estado === 'entrevista' ? 'primary' :
                                                            postulacion.estado === 'contratado' ? 'success' :
                                                                postulacion.estado === 'rechazado' ? 'danger' : 'secondary'
                                            }>
                                                {postulacion.estado === 'pendiente' ? 'Pendiente' :
                                                    postulacion.estado === 'revisado' ? 'Revisado' :
                                                        postulacion.estado === 'entrevista' ? 'Entrevista' :
                                                            postulacion.estado === 'contratado' ? 'Contratado' :
                                                                postulacion.estado === 'rechazado' ? 'Rechazado' : postulacion.estado}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => navigate(`/candidatos/${postulacion.candidatoId}`)}
                                            >
                                                Ver candidato
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </>
        );
    }
};

export default VacanteFormScreen;
